import { useCallback, useState } from 'react';

import { useToast } from '@/components/feedback/toast';
import { ApiError } from '@/lib/api/errors';
import { tokenStore } from '@/lib/api/token-store';
import { useAuth } from '@/providers/auth-provider';

import {
  resendCurrentPhoneOtp,
  resendNewPhoneOtp,
  sendCurrentPhoneOtp,
  verifyCurrentPhoneOtp,
  verifyNewPhoneOtp,
} from '../api';
import { PROFILE_COPY, PROFILE_ERROR_CODES } from '../constants';
import type { ProfileOtpDispatch } from '../schemas';
import { useProfileModalStore } from '../use-profile-modal-store';

import { NewNumberStep } from './new-number-step';
import { SendCodeStep } from './send-code-step';
import { VerificationCodeStep } from './verification-code-step';

type Step =
  | { name: 'current' }
  | { name: 'code'; dispatch: ProfileOtpDispatch }
  | { name: 'new'; rejected?: { phone: string; message: string } }
  | { name: 'code-new'; dispatch: ProfileOtpDispatch; phone: string };

const COPY = PROFILE_COPY.changePhone;

/** 117854 Step 1. Every exit — Cancel, ✕, Escape, expiry — goes back to Edit Profile (grilling Q9),
 *  whose remount reloads the profile (`useProfile` has `gcTime: 0`, §8a). Unmounting this component
 *  is what makes a reopen start again at `current` (§8c). */
export function ChangePhoneFlow() {
  const setOpen = useProfileModalStore((s) => s.setOpen);
  const [step, setStep] = useState<Step>({ name: 'current' });
  const backToEdit = useCallback(() => setOpen('edit'), [setOpen]);
  const toast = useToast();
  const { clearSession } = useAuth();

  const verifyNew = (phone: string) => async (body: { referenceId: string; otp: string }) => {
    try {
      await verifyNewPhoneOtp(body);
    } catch (error) {
      if (error instanceof ApiError && error.code === PROFILE_ERROR_CODES.phoneAlreadyExists) {
        setStep({ name: 'new', rejected: { phone, message: COPY.newNumber.alreadyInUse } }); // §7f
        return;
      }
      throw error; // 422 / expiry / 503 — the shared step's branches
    }
    // §7a/§7b: every session is already revoked server-side; end this one locally, the way a
    // successful Sign Out does — never through sessionExpiry, so no expiry copy (grilling Q9).
    toast.success({ title: COPY.updated });
    setOpen(null); // the global store would otherwise reopen this flow on the next login
    tokenStore.clear();
    clearSession(); // <RequireAuth> redirects to login
  };

  if (step.name === 'current') {
    return (
      <SendCodeStep
        title={COPY.title}
        helper={COPY.helper}
        send={sendCurrentPhoneOtp}
        onSent={(dispatch) => setStep({ name: 'code', dispatch })}
        onCancel={backToEdit}
      />
    );
  }
  if (step.name === 'code') {
    return (
      <VerificationCodeStep
        dispatch={step.dispatch}
        resendLabel={COPY.resendLabel}
        verify={async (body) => {
          await verifyCurrentPhoneOtp(body);
          setStep({ name: 'new' });
        }}
        resend={resendCurrentPhoneOtp}
        onExpired={backToEdit}
        onCancel={backToEdit}
      />
    );
  }
  if (step.name === 'new') {
    return (
      <NewNumberStep
        initial={step.rejected}
        onSent={(dispatch, phone) => setStep({ name: 'code-new', dispatch, phone })}
        onExpired={backToEdit}
        onCancel={backToEdit}
      />
    );
  }
  return (
    <VerificationCodeStep
      dispatch={step.dispatch}
      resendLabel={COPY.resendLabel}
      verify={verifyNew(step.phone)}
      resend={resendNewPhoneOtp}
      onExpired={backToEdit}
      onCancel={backToEdit}
    />
  );
}
